import { useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Box,
  Collapse,
  FormControl,
  InputLabel,
  NativeSelect,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useIsTab } from "hooks/is-tab.hook";

dayjs.extend(utc);

export default function Timeframe({
  timeframeValue,
  setTimeframeValue,
  startDateValue,
  setStartDateValue,
  endDateValue,
  setEndDateValue,
  frequencies,
  disabled = false,
  sx,
  isOpen = true,
}) {
  const isTab = useIsTab();

  const [timeframe, setTimeframe] = useState(0);
  const [startDate, setStartDate] = useState(dayjs().subtract(1, "month"));
  const [endDate, setEndDate] = useState(dayjs());
  const [startDateError, setStartDateError] = useState(false);
  const [endDateError, setEndDateError] = useState(false);

  function handleTimeframeChange(event) {
    setTimeframeValue(event.target.value);
    // Reset error states when timeframe changes
    setStartDateError(false);
    setEndDateError(false);
  }
  function handleStartDateChange(date) {
    if (date && date.isValid()) {
      setStartDateValue(date.utc().format());
      setStartDateError(false);
    } else {
      setStartDateValue(null);
      setStartDateError(true);
    }
  }
  function handleEndDateChange(date) {
    if (date && date.isValid()) {
      setEndDateValue(date.utc().format());
      setEndDateError(false);
    } else {
      setEndDateValue(null);
      setEndDateError(true);
    }
  }

  useEffect(() => {
    setTimeframe(timeframeValue);
    if (timeframeValue === "custom-date") {
      // If no dates are provided, set default dates
      if (!startDateValue && !endDateValue) {
        const defaultStartDate = dayjs().subtract(1, "month");
        const defaultEndDate = dayjs();
        setStartDateValue(defaultStartDate.utc().format());
        setEndDateValue(defaultEndDate.utc().format());
        setStartDate(defaultStartDate);
        setEndDate(defaultEndDate);
      } else {
        setStartDate(startDateValue ? dayjs(startDateValue) : dayjs().subtract(1, "month"));
        setEndDate(endDateValue ? dayjs(endDateValue) : dayjs());
      }
    } else {
      // Reset error states when not using custom date
      setStartDateError(false);
      setEndDateError(false);
    }
  // Intentional: this initialization must run only when the selected timeframe
  // changes; date value/state updates are synchronized by the effects below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframeValue]);

  useEffect(() => {
    if (startDateValue != null) {
      setStartDate(dayjs(startDateValue));
    }
  }, [startDateValue]);

  useEffect(() => {
    if (endDateValue != null) {
      setEndDate(dayjs(endDateValue));
    }
  }, [endDateValue]);

  useEffect(() => {
    if (!isOpen) {
      // Reset error states when search bar closes
      setStartDateError(false);
      setEndDateError(false);
    }
  }, [isOpen]);

  return (
    <Stack
      direction={isTab ? "row" : "column"}
      alignItems="stretch"
      mt={1}
      sx={sx}
      onKeyUp={(event) => event.stopPropagation()}
    >
      <Box>
        <FormControl fullWidth={!isTab} disabled={disabled}>
          <InputLabel
            htmlFor="timeframe"
            size="small"
          >
            Timeframe
          </InputLabel>
          <NativeSelect
            size="small"
            input={<OutlinedInput/>}
            value={timeframe}
            onChange={handleTimeframeChange}
            id="timeframe"
            label="Timeframe"
          >
            <option value={0} disabled>
              Select Timeframe
            </option>
            {frequencies.map((frequency) => {
              return (
                <option
                  key={`timeframe-key-${frequency.key}`}
                  value={frequency.key}
                >
                  {frequency.label}
                </option>
              );
            })}
          </NativeSelect>
        </FormControl>
      </Box>
      <Collapse in={timeframe === "custom-date"}>
        <Stack direction={isTab ? "row" : "column"} alignItems="stretch">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              onChange={handleStartDateChange}
              value={startDate}
              maxDate={endDate}
              slotProps={{
                textField: {
                  error: startDateError,
                  helperText: startDateError ? "Invalid Date" : "",
                },
              }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="End Date"
              onChange={handleEndDateChange}
              value={endDate}
              minDate={startDate}
              slotProps={{
                textField: {
                  error: endDateError,
                  helperText: endDateError ? "Invalid Date" : "",
                },
              }}
            />
          </LocalizationProvider>
        </Stack>
      </Collapse>
    </Stack>
  );
}
